import React, { useMemo } from 'react'
import classnames from 'classnames'

import {
  MenuConfig,
  MenuTemplate,
  PlaceHolderImageGenerationEnum
} from 'src/apollo/sites'
import { formatImageURL } from 'src/lib/js/utilities'

import Image from 'shared/components/common/Image'

import { DefaultPlaceHolderMenuImage } from 'public/components/default_template/menu_section/DefaultPlaceHolderMenuImage'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import { TemplateOverrides } from 'public/components/default_template/menu_section/menuStylingOverrides'
import { useImageAspectRatio } from 'src/shared/js/hooks/useImageAspectRatio'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'

export const IMAGE_SUFFIX_REGEX = /\.(jpg|png)$/

export type ToastImageSet = {
  xs?: string
  small?: string
  medium?: string
  large?: string
  xl?: string
  xxl?: string
  raw?: string
}

type MenuItemImageSet = {
  '140w'?: string
  '300w'?: string
  '600w'?: string
  '900w'?: string
}

// These source set URLs are served by the toast photo service
// and are guaranteed to exist
// TODO: (https://toasttab.atlassian.net/browse/WOO-3544): Add a sizes tag to the
// images in order to optimize loading times. srcSet without sizes always assumes
// that the image is displayed at 100% width.
export const getMenuItemSrcSet = (images: ToastImageSet): MenuItemImageSet => {
  const imgSet: MenuItemImageSet = {}
  if (images.xs) {
    imgSet['140w'] = formatImageURL(images.xs)
  }
  if (images.small) {
    imgSet['300w'] = formatImageURL(images.small)
  }
  if (images.medium) {
    imgSet['600w'] = formatImageURL(images.medium)
  }
  if (images.large) {
    imgSet['900w'] = formatImageURL(images.large)
  }
  return imgSet
}

export const getTemplateClass = (
  template: MenuTemplate | undefined | null
): string => {
  if (!template) return ''
  const classStr = template ? template.toString() : ''
  if (!classStr) return ''
  return classStr.charAt(0).toLowerCase() + classStr.slice(1)
}

type ItemContentProps = {
  item: MenuItem
  menuGuid?: string | null
  menuConfig?: MenuConfig | null
  templateOverrides?: TemplateOverrides
  isReadOnly?: boolean
  style?: React.CSSProperties
}

export const MenuItemImage = (props: ItemContentProps) => {
  const { item, templateOverrides } = props
  const { ooImageAspectRatio, ooImageAspectRatioThreshold } = useFlags()

  // Detect actual image aspect ratio to intelligently apply object-fit
  // Only apply when feature flag is enabled
  // Parse threshold from feature flag, default to 0.7 if not provided or invalid
  const threshold = parseFloat(ooImageAspectRatioThreshold || '0.7') || 0.7
  const { aspectRatioClass: detectedAspectRatioClass, onImageLoad } =
    useImageAspectRatio(threshold)
  const imageAspectRatioClass = ooImageAspectRatio
    ? detectedAspectRatioClass
    : undefined

  const imageSrc = useMemo(() => {
    const imgSrc =
      item.imageUrls?.medium ||
      item.imageUrls?.small ||
      item.imageUrls?.large ||
      item.imageUrls?.xs
    const formattedImgSrc = imgSrc && formatImageURL(imgSrc)
    return item?.image || formattedImgSrc
  }, [item?.image, item.imageUrls])

  const itemImage = useMemo(() => {
    const imgSrcSet = item.imageUrls && getMenuItemSrcSet(item.imageUrls)
    const className = classnames('itemImage', imageAspectRatioClass)

    if (item?.image) {
      return (
        <Image
          alt=''
          src={item.image}
          className={className}
          data-testid={item.name + '-image'}
          style={props.style}
          onLoad={ooImageAspectRatio ? onImageLoad : undefined}
        />
      )
    }
    if (imageSrc && imgSrcSet) {
      return (
        <Image
          src={imageSrc}
          srcSet={imgSrcSet}
          className={className}
          alt=''
          style={props.style}
          onLoad={ooImageAspectRatio ? onImageLoad : undefined}
        />
      )
    } else if (
      templateOverrides?.placeholderImageConfig.generatePlaceholderImage ===
      PlaceHolderImageGenerationEnum.RepeatedText
    ) {
      return (
        <DefaultPlaceHolderMenuImage
          className='itemImage'
          templateOverrides={templateOverrides}
        />
      )
    } else {
      return null
    }
  }, [
    item,
    templateOverrides,
    imageAspectRatioClass,
    imageSrc,
    onImageLoad,
    props.style,
    ooImageAspectRatio
  ])

  return itemImage
}
